import { api } from '@/lib/api';
import { compressVideoTo720p } from '@/lib/videoCompress';

const CHUNK = 5 * 1024 * 1024; // 5 MB chunks to the backend (Object Store flow)
const CHUNK_TIMEOUT = 45 * 1000; // per-request network timeout
const CHUNK_RETRIES = 3;
const UPLOAD_DEADLINE_MS = 3 * 60 * 1000; // whole upload must resolve within 3 minutes

function timeoutError() {
  const err = new Error('Video upload timed out — please check your connection and try again.');
  err.response = { data: { detail: err.message } };
  return err;
}

async function postChunkWithRetry(url, body, config, deadline) {
  let attempt = 0;
  for (;;) {
    if (Date.now() > deadline) throw timeoutError();
    try {
      return await api.post(url, body, { ...config, timeout: CHUNK_TIMEOUT });
    } catch (e) {
      attempt += 1;
      const status = e?.response?.status;
      const retryable = !status || status === 408 || status === 429 || status >= 500;
      if (!retryable || attempt >= CHUNK_RETRIES || Date.now() > deadline) {
        if (e.code === 'ECONNABORTED') throw timeoutError();
        throw e;
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 1000 * 2 ** (attempt - 1)));
    }
  }
}

function capturePoster(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    let done = false;
    const finish = (blob) => {
      if (done) return;
      done = true;
      URL.revokeObjectURL(url);
      resolve(blob || null);
    };
    const timer = setTimeout(() => finish(null), 8000);
    v.muted = true;
    v.playsInline = true;
    v.preload = 'auto';
    v.onerror = () => { clearTimeout(timer); finish(null); };
    v.onloadeddata = () => {
      try { v.currentTime = Math.min(0.1, (v.duration || 1) / 2); } catch { clearTimeout(timer); finish(null); }
    };
    v.onseeked = () => {
      try {
        const scale = Math.min(1, 1280 / (v.videoWidth || 1280));
        const c = document.createElement('canvas');
        c.width = Math.max(1, Math.round((v.videoWidth || 1280) * scale));
        c.height = Math.max(1, Math.round((v.videoHeight || 720) * scale));
        c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
        c.toBlob((b) => { clearTimeout(timer); finish(b); }, 'image/jpeg', 0.8);
      } catch { clearTimeout(timer); finish(null); }
    };
    v.src = url;
  });
}

export async function uploadVideoFile(file, onProgress) {
  const deadline = Date.now() + UPLOAD_DEADLINE_MS;
  const report = (p) => { if (onProgress) onProgress(p); };
  const compressed = await compressVideoTo720p(file, (p) => report(Math.round(p * 0.4)));
  const upload = compressed || file;
  const base = compressed !== file ? 40 : 0;
  const span = 95 - base;

  const { data: start } = await api.post('/stories/video/start', {
    mime_type: upload.type || 'video/mp4',
    total_size: upload.size,
  }, { timeout: 30000 });
  const uploadId = start.upload_id;

  let chunkIndex = 0;
  for (let offset = 0; offset < upload.size; offset += CHUNK) {
    if (Date.now() > deadline) throw timeoutError();
    const end = Math.min(offset + CHUNK, upload.size);
    // eslint-disable-next-line no-await-in-loop
    await postChunkWithRetry(
      `/stories/video/${uploadId}/chunk?chunk_index=${chunkIndex}`,
      upload.slice(offset, end),
      { headers: { 'Content-Type': 'application/octet-stream' } },
      deadline,
    );
    chunkIndex += 1;
    report(base + Math.min(span, Math.round((end / upload.size) * span)));
  }

  const { data: completed } = await postChunkWithRetry(
    `/stories/video/${uploadId}/complete`,
    null,
    { timeout: 120000 },
    Date.now() + 120000,
  );
  report(97);

  let posterUrl = null;
  try {
    const poster = await capturePoster(upload);
    if (poster && poster.size <= 2 * 1024 * 1024) {
      const { data: p } = await api.post(`/stories/video/${uploadId}/poster`, poster, {
        headers: { 'Content-Type': 'image/jpeg' },
        timeout: 60000,
      });
      posterUrl = p.poster_url || null;
    }
  } catch {
    // poster is best-effort — the video still posts without one
  }

  report(100);
  return {
    video_url: completed.video_url,
    poster_url: posterUrl,
  };
}
